/**
 * Advisor Lead Service
 *
 * Solicitudes de asesores comerciales (lead capture) — espejo de
 * `src/modules/advisor-lead` del backend. Gestionadas por el equipo de
 * soporte; la conversión crea el vínculo con un Staff COMMERCIAL_ADVISOR.
 */

import { apiClient } from './api.service';
import type {
  AdvisorLead,
  AdvisorLeadQueryParams,
  AdvisorLeadPaginatedResponse,
  AssignAdvisorLeadDto,
  UpdateAdvisorLeadStatusDto,
  AddAdvisorLeadNoteDto,
  ConvertAdvisorLeadDto,
  ApproveAdvisorLeadDto,
  ApproveAdvisorLeadResult,
  AdvisorApprovalContext,
} from '@/types/advisor-lead.types';

class AdvisorLeadService {
  private readonly basePath = '/advisor-leads';

  async getAll(
    params: AdvisorLeadQueryParams = {},
    opts: { signal?: AbortSignal } = {},
  ): Promise<AdvisorLeadPaginatedResponse> {
    const sp = new URLSearchParams();
    if (params.page) sp.set('page', String(params.page));
    if (params.limit) sp.set('limit', String(params.limit));
    if (params.status) sp.set('status', params.status);
    if (params.search) sp.set('search', params.search);
    if (params.assignedToStaffId)
      sp.set('assignedToStaffId', params.assignedToStaffId);
    if (params.pendingOnly) sp.set('pendingOnly', 'true');

    const query = sp.toString();
    const endpoint = query ? `${this.basePath}?${query}` : this.basePath;

    const response = await apiClient.get<AdvisorLeadPaginatedResponse>(
      endpoint,
      { signal: opts.signal },
    );
    return response.data;
  }

  async getById(
    id: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<AdvisorLead> {
    const response = await apiClient.get<AdvisorLead>(
      `${this.basePath}/${id}`,
      { signal: opts.signal },
    );
    return response.data;
  }

  async assign(id: string, dto: AssignAdvisorLeadDto): Promise<AdvisorLead> {
    const response = await apiClient.patch<AdvisorLead>(
      `${this.basePath}/${id}/assign`,
      dto,
    );
    return response.data;
  }

  async updateStatus(
    id: string,
    dto: UpdateAdvisorLeadStatusDto,
  ): Promise<AdvisorLead> {
    const response = await apiClient.patch<AdvisorLead>(
      `${this.basePath}/${id}/status`,
      dto,
    );
    return response.data;
  }

  async addNote(id: string, dto: AddAdvisorLeadNoteDto): Promise<AdvisorLead> {
    const response = await apiClient.post<AdvisorLead>(
      `${this.basePath}/${id}/notes`,
      dto,
    );
    return response.data;
  }

  /**
   * Datos para pre-llenar el formulario de aprobación: nombre partido,
   * cuenta de la app que coincide (y si es creador de contenido) y los
   * motivos por los que el alta fallaría.
   */
  async getApprovalContext(
    id: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<AdvisorApprovalContext> {
    const response = await apiClient.get<AdvisorApprovalContext>(
      `${this.basePath}/${id}/approval-context`,
      { signal: opts.signal },
    );
    return response.data;
  }

  /**
   * Aprueba la postulación y **crea el aliado**: User + Staff
   * (COMMERCIAL_ADVISOR) + perfil financiero, y sella el lead. Es el camino
   * principal. Roles: SUPER_ADMIN / SUPPORT_LEAD.
   */
  async approve(
    id: string,
    dto: ApproveAdvisorLeadDto,
  ): Promise<ApproveAdvisorLeadResult> {
    const response = await apiClient.post<ApproveAdvisorLeadResult>(
      `${this.basePath}/${id}/approve`,
      dto,
    );
    return response.data;
  }

  /**
   * Camino secundario: vincula el lead a un Staff COMMERCIAL_ADVISOR que ya
   * existe. Para dar de alta a un aliado nuevo se usa `approve`.
   */
  async convert(id: string, dto: ConvertAdvisorLeadDto): Promise<AdvisorLead> {
    const response = await apiClient.post<AdvisorLead>(
      `${this.basePath}/${id}/convert`,
      dto,
    );
    return response.data;
  }
}

export const advisorLeadService = new AdvisorLeadService();
